import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useOrg } from "@/context/OrgContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash, Plus, GlobeSimple, CurrencyEur, Package, FileArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API_BASE } from "@/lib/api";

const YEARS = [2026, 2027, 2028, 2029, 2030, 2031, 2032, 2033, 2034];
const QUARTERS = [
  { key: "all", label: "Yıllık" },
  { key: "1", label: "Q1 (Oca-Mar)" },
  { key: "2", label: "Q2 (Nis-Haz)" },
  { key: "3", label: "Q3 (Tem-Eyl)" },
  { key: "4", label: "Q4 (Eki-Ara)" },
];

export default function Cbam() {
  const { t } = useLang();
  const { role } = useOrg();
  const [year, setYear] = useState(new Date().getFullYear() >= 2026 ? new Date().getFullYear() : 2026);
  const [quarter, setQuarter] = useState("all");
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [imports, setImports] = useState([]);
  const [liability, setLiability] = useState(null);
  const [quarterly, setQuarterly] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [priceEdit, setPriceEdit] = useState("");
  const [form, setForm] = useState({
    product_category: "",
    cn_code: "",
    product_description: "",
    quantity: "",
    country_of_origin: "",
    embedded_direct_tco2e_per_unit: "",
    embedded_indirect_tco2e_per_unit: "",
    carbon_price_paid_origin_eur_per_tco2e: 0,
    date: new Date().toISOString().slice(0, 10),
    notes: "",
  });

  const load = async () => {
    const calls = [
      api.get("/cbam/products"),
      api.get("/cbam/settings"),
      api.get("/cbam/imports", { params: { year } }),
      api.get("/cbam/liability", { params: { year } }),
    ];
    if (quarter !== "all") {
      calls.push(api.get(`/cbam/quarterly/${year}/${quarter}`));
    }
    const results = await Promise.all(calls);
    setProducts(results[0].data);
    setSettings(results[1].data);
    setImports(results[2].data);
    setLiability(results[3].data);
    setPriceEdit(String(results[1].data.eu_ets_price_eur_per_tco2e));
    if (quarter !== "all") setQuarterly(results[4].data);
    else setQuarterly(null);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, quarter]);

  const downloadQuarterlyPdf = () => {
    if (quarter === "all") {
      toast.info(t("cbamSelectQuarter") || "Select a quarter first (Q1-Q4)");
      return;
    }
    const url = `${API_BASE}/cbam/quarterly/${year}/${quarter}/pdf`;
    const token = localStorage.getItem("cl_token") || "";
    const orgId = localStorage.getItem("cl_active_org") || "";
    fetch(url, {
      credentials: "include",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(orgId ? { "X-Org-Id": orgId } : {}),
      },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `cbam_Q${quarter}_${year}.pdf`;
        link.click();
      });
  };

  const downloadQuarterlyXml = () => {
    if (quarter === "all") {
      toast.info(t("cbamSelectQuarter") || "Select a quarter first (Q1-Q4)");
      return;
    }
    const url = `${API_BASE}/cbam/quarterly/${year}/${quarter}/xml`;
    const token = localStorage.getItem("cl_token") || "";
    const orgId = localStorage.getItem("cl_active_org") || "";
    fetch(url, {
      credentials: "include",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(orgId ? { "X-Org-Id": orgId } : {}),
      },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `cbam_Q${quarter}_${year}.xml`;
        link.click();
      });
  };

  const downloadQuarterlyFormat = (fmt) => {
    if (quarter === "all") {
      toast.info(t("cbamSelectQuarter"));
      return;
    }
    const url = `${API_BASE}/cbam/quarterly/${year}/${quarter}/${fmt}`;
    const token = localStorage.getItem("cl_token") || "";
    const orgId = localStorage.getItem("cl_active_org") || "";
    fetch(url, {
      credentials: "include",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(orgId ? { "X-Org-Id": orgId } : {}),
      },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `cbam_Q${quarter}_${year}.${fmt}`;
        link.click();
      });
  };

  const selectedProduct = useMemo(
    () => products.find((p) => p.key === form.product_category),
    [products, form.product_category],
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_category || !form.quantity || !form.country_of_origin) return;
    try {
      const payload = {
        product_category: form.product_category,
        cn_code: form.cn_code || null,
        product_description: form.product_description || null,
        quantity: parseFloat(form.quantity),
        country_of_origin: form.country_of_origin.trim(),
        embedded_direct_tco2e_per_unit: form.embedded_direct_tco2e_per_unit === "" ? null : parseFloat(form.embedded_direct_tco2e_per_unit),
        embedded_indirect_tco2e_per_unit: form.embedded_indirect_tco2e_per_unit === "" ? null : parseFloat(form.embedded_indirect_tco2e_per_unit),
        carbon_price_paid_origin_eur_per_tco2e: parseFloat(form.carbon_price_paid_origin_eur_per_tco2e || 0),
        date: form.date,
        notes: form.notes || null,
      };
      await api.post("/cbam/imports", payload);
      toast.success(t("cbamImportAdded"));
      setShowForm(false);
      setForm({ ...form, product_category: "", cn_code: "", product_description: "", quantity: "", country_of_origin: "", embedded_direct_tco2e_per_unit: "", embedded_indirect_tco2e_per_unit: "", notes: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const remove = async (id) => {
    await api.delete(`/cbam/imports/${id}`);
    toast.success(t("cbamImportDeleted"));
    load();
  };

  const savePrice = async () => {
    const p = parseFloat(priceEdit);
    if (!(p > 0)) return;
    try {
      await api.put("/cbam/settings", { eu_ets_price_eur_per_tco2e: p });
      toast.success(t("cbamPriceUpdated"));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  if (!settings || !liability) return <div className="text-muted-foreground">Loading…</div>;

  // Effective view: quarterly if selected, else annual liability
  const activeTotals = quarterly ? quarterly.totals : (liability.totals || {});
  const activePhaseIn = quarterly ? quarterly.phase_in_factor : liability.phase_in_factor;
  const activeImports = quarter === "all"
    ? imports
    : imports.filter((i) => {
        const m = parseInt((i.date || "").slice(5, 7), 10);
        if (!m) return false;
        return Math.ceil(m / 3) === parseInt(quarter, 10);
      });
  const periodLabel = quarterly ? quarterly.period_label : `${year}`;
  const totals = activeTotals;

  return (
    <div className="space-y-6" data-testid="cbam-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">EU Regulation 2023/956</div>
          <h1 className="font-display text-3xl font-semibold mt-1">{t("cbamTitle")}</h1>
          <p className="text-muted-foreground text-sm mt-2">{t("cbamDesc")}</p>
        </div>
        <div className="flex gap-2 items-end flex-wrap">
          <div className="space-y-1.5">
            <Label htmlFor="cbam-year" className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">{t("year")}</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
              <SelectTrigger id="cbam-year" className="w-[110px] font-mono-data" data-testid="cbam-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {YEARS.map((y) => (
                  <SelectItem key={`cbam-y-${y}`} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cbam-quarter" className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">{t("cbamQuarter")}</Label>
            <Select value={quarter} onValueChange={setQuarter}>
              <SelectTrigger id="cbam-quarter" className="w-[170px] font-mono-data" data-testid="cbam-quarter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {QUARTERS.map((q) => (
                  <SelectItem key={`cbam-q-${q.key}`} value={q.key}>{q.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            onClick={downloadQuarterlyPdf}
            disabled={quarter === "all"}
            data-testid="cbam-quarterly-pdf"
            title={quarter === "all" ? t("cbamSelectQuarter") : ""}
          >
            <FileArrowDown size={16} className="mr-2" />
            {t("cbamDownloadPdf")}
          </Button>
          <Button
            variant="outline"
            onClick={downloadQuarterlyXml}
            disabled={quarter === "all"}
            data-testid="cbam-quarterly-xml"
            title={quarter === "all" ? t("cbamSelectQuarter") : ""}
          >
            <FileArrowDown size={16} className="mr-2" />
            {t("cbamDownloadXml")}
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadQuarterlyFormat("csv")}
            disabled={quarter === "all"}
            data-testid="cbam-quarterly-csv"
          >
            <FileArrowDown size={16} className="mr-2" />
            {t("cbamDownloadCsv")}
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadQuarterlyFormat("json")}
            disabled={quarter === "all"}
            data-testid="cbam-quarterly-json"
          >
            <FileArrowDown size={16} className="mr-2" />
            {t("cbamDownloadJson")}
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="lg:col-span-2 border-primary/30" data-testid="cbam-kpi-payable">
          <CardContent className="p-6">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data flex items-center gap-2">
              <CurrencyEur size={14} weight="duotone" className="text-primary" />
              {t("cbamPayable")}
            </div>
            <div className="font-display text-5xl font-semibold mt-2 leading-none font-mono-data">
              €{totals.cbam_payable_eur?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || "0.00"}
            </div>
            <div className="text-xs text-muted-foreground mt-3 font-mono-data">
              {totals.records || 0} {t("declarations")} · {t("phaseIn")} {(activePhaseIn * 100).toFixed(1)}% · {periodLabel}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="cbam-kpi-embedded">
          <CardContent className="p-6">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">{t("cbamEmbedded")}</div>
            <div className="font-display text-3xl font-semibold mt-2 font-mono-data">
              {(totals.embedded_tco2e || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
              <span className="text-sm text-muted-foreground ml-1">tCO₂e</span>
            </div>
            <div className="text-xs text-muted-foreground mt-2 font-mono-data">
              {t("cbamGross")}: €{(totals.liability_gross_eur || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="cbam-kpi-origin">
          <CardContent className="p-6">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">{t("cbamOriginCredit")}</div>
            <div className="font-display text-3xl font-semibold mt-2 font-mono-data">
              €{(totals.origin_credit_eur || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
            <div className="text-xs text-muted-foreground mt-2">{t("cbamOriginCreditHint")}</div>
          </CardContent>
        </Card>
      </div>

      {/* Settings — EU ETS price */}
      <Card>
        <CardHeader>
          <CardTitle className="font-display text-lg flex items-center gap-2">
            <CurrencyEur size={20} weight="duotone" className="text-primary" />
            {t("cbamEtsPriceTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-3 flex-wrap">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">{t("cbamEtsPrice")}</Label>
              <div className="flex gap-2 items-center">
                <span className="font-mono-data text-sm text-muted-foreground">€</span>
                <Input
                  type="number"
                  step="0.01"
                  className="w-32 font-mono-data"
                  value={priceEdit}
                  onChange={(e) => setPriceEdit(e.target.value)}
                  disabled={role !== "owner"}
                  data-testid="cbam-price-input"
                />
                <span className="font-mono-data text-xs text-muted-foreground">/tCO₂e</span>
              </div>
            </div>
            {role === "owner" && (
              <Button onClick={savePrice} size="sm" data-testid="cbam-price-save">{t("save")}</Button>
            )}
            <div className="text-xs text-muted-foreground max-w-md ml-4">{t("cbamEtsPriceHint")}</div>
          </div>
        </CardContent>
      </Card>

      {/* Add declaration form */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-display text-lg">{t("cbamAddImport")}</CardTitle>
          {(role === "owner" || role === "editor") && (
            <Button
              variant={showForm ? "outline" : "default"}
              size="sm"
              onClick={() => setShowForm((s) => !s)}
              data-testid="cbam-toggle-form"
            >
              <Plus size={16} className="mr-1" /> {showForm ? t("cancel") : t("cbamAddImport")}
            </Button>
          )}
        </CardHeader>
        {showForm && (
          <CardContent>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="cbam-form">
              <div className="space-y-2">
                <Label>{t("cbamProduct")}</Label>
                <Select
                  value={form.product_category}
                  onValueChange={(v) => setForm({ ...form, product_category: v })}
                >
                  <SelectTrigger data-testid="cbam-product-select">
                    <SelectValue placeholder={t("cbamProduct")} />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((p) => (
                      <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("cbamCnCode")}</Label>
                <Input
                  value={form.cn_code}
                  onChange={(e) => setForm({ ...form, cn_code: e.target.value })}
                  placeholder={selectedProduct ? selectedProduct.cn_prefixes?.[0] : "e.g. 720610"}
                  data-testid="cbam-cn-code"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("cbamQuantity")} {selectedProduct && <span className="text-muted-foreground text-xs">({selectedProduct.unit})</span>}</Label>
                <Input
                  type="number"
                  step="any"
                  min="0"
                  required
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  data-testid="cbam-quantity"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("cbamCountry")}</Label>
                <Input
                  required
                  value={form.country_of_origin}
                  onChange={(e) => setForm({ ...form, country_of_origin: e.target.value })}
                  placeholder="e.g. Türkiye, India, China"
                  data-testid="cbam-country"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("cbamDirectOverride")} {selectedProduct && <span className="text-muted-foreground text-xs">({t("default")}: {selectedProduct.direct_default} tCO₂e/{selectedProduct.unit})</span>}</Label>
                <Input
                  type="number"
                  step="any"
                  min="0"
                  value={form.embedded_direct_tco2e_per_unit}
                  onChange={(e) => setForm({ ...form, embedded_direct_tco2e_per_unit: e.target.value })}
                  placeholder={selectedProduct ? String(selectedProduct.direct_default) : ""}
                  data-testid="cbam-direct"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("cbamIndirectOverride")} {selectedProduct && <span className="text-muted-foreground text-xs">({t("default")}: {selectedProduct.indirect_default} tCO₂e/{selectedProduct.unit})</span>}</Label>
                <Input
                  type="number"
                  step="any"
                  min="0"
                  value={form.embedded_indirect_tco2e_per_unit}
                  onChange={(e) => setForm({ ...form, embedded_indirect_tco2e_per_unit: e.target.value })}
                  placeholder={selectedProduct ? String(selectedProduct.indirect_default) : ""}
                  data-testid="cbam-indirect"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("cbamOriginPrice")} <span className="text-muted-foreground text-xs">(€/tCO₂e)</span></Label>
                <Input
                  type="number"
                  step="any"
                  min="0"
                  value={form.carbon_price_paid_origin_eur_per_tco2e}
                  onChange={(e) => setForm({ ...form, carbon_price_paid_origin_eur_per_tco2e: e.target.value })}
                  data-testid="cbam-origin-price"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("date")}</Label>
                <Input
                  type="date"
                  required
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  data-testid="cbam-date"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>{t("productDescription") || "Product description"}</Label>
                <Input
                  value={form.product_description}
                  onChange={(e) => setForm({ ...form, product_description: e.target.value })}
                  placeholder="e.g. Rebar 12mm — supplier X"
                  data-testid="cbam-description"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>{t("notes")}</Label>
                <Textarea
                  rows={1}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="cbam-notes"
                />
              </div>
              {/* Live preview */}
              {selectedProduct && form.quantity && (
                <div className="md:col-span-2 border border-primary/30 rounded-md p-4 bg-primary/5" data-testid="cbam-preview">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data mb-2">{t("cbamPreview")}</div>
                  <CbamPreview
                    product={selectedProduct}
                    quantity={parseFloat(form.quantity || 0)}
                    directOverride={form.embedded_direct_tco2e_per_unit}
                    indirectOverride={form.embedded_indirect_tco2e_per_unit}
                    originPrice={parseFloat(form.carbon_price_paid_origin_eur_per_tco2e || 0)}
                    etsPrice={settings.eu_ets_price_eur_per_tco2e}
                    phaseIn={activePhaseIn}
                  />
                </div>
              )}
              <div className="md:col-span-2 flex justify-end">
                <Button type="submit" data-testid="cbam-submit">{t("save")}</Button>
              </div>
            </form>
          </CardContent>
        )}
      </Card>

      {/* Declarations table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-display text-lg">{t("cbamDeclarations")} — {activeImports.length} · {periodLabel}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {activeImports.length === 0 ? (
            <div className="p-8 text-sm text-muted-foreground text-center">{t("cbamNoImports")}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                  <tr>
                    <th className="text-left px-4 py-2">{t("date")}</th>
                    <th className="text-left px-4 py-2">{t("cbamProduct")}</th>
                    <th className="text-left px-4 py-2">{t("cbamCountry")}</th>
                    <th className="text-right px-4 py-2">{t("cbamQuantity")}</th>
                    <th className="text-right px-4 py-2">tCO₂e</th>
                    <th className="text-right px-4 py-2">{t("cbamPayable")} (€)</th>
                    <th className="text-right px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {activeImports.map((i) => (
                    <tr key={i.id} className="border-b border-border/60 hover:bg-secondary/40 transition-colors duration-150" data-testid={`cbam-row-${i.id}`}>
                      <td className="px-4 py-2 font-mono-data">{i.date}</td>
                      <td className="px-4 py-2">
                        <div>{i.product_name}</div>
                        {i.cn_code && <div className="text-xs text-muted-foreground font-mono-data">CN {i.cn_code}</div>}
                        {i.product_description && <div className="text-xs text-muted-foreground italic">"{i.product_description}"</div>}
                      </td>
                      <td className="px-4 py-2">{i.country_of_origin}</td>
                      <td className="px-4 py-2 text-right font-mono-data">
                        {i.quantity} <span className="text-muted-foreground text-xs">{i.unit}</span>
                      </td>
                      <td className="px-4 py-2 text-right font-mono-data">{i.embedded_total_tco2e.toFixed(3)}</td>
                      <td className="px-4 py-2 text-right font-mono-data font-medium">
                        €{i.cbam_payable_eur.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {(role === "owner" || role === "editor") && (
                          <button
                            onClick={() => remove(i.id)}
                            className="text-muted-foreground hover:text-destructive transition-colors"
                            data-testid={`cbam-delete-${i.id}`}
                          >
                            <Trash size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Breakdown by product / country */}
      {liability.by_product && liability.by_product.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="font-display text-lg flex items-center gap-2">
                <Package size={20} weight="duotone" className="text-primary" />
                {t("cbamByProduct")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2" data-testid="cbam-by-product">
                {liability.by_product.map((p) => {
                  const pct = totals.cbam_payable_eur > 0 ? (p.cbam_payable_eur / totals.cbam_payable_eur) * 100 : 0;
                  return (
                    <div key={p.product_category} className="flex items-center gap-4">
                      <div className="w-44 truncate text-sm">{p.product_name}</div>
                      <div className="flex-1 h-6 bg-secondary rounded-sm overflow-hidden">
                        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="w-32 text-right font-mono-data text-sm">
                        €{p.cbam_payable_eur.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </div>
                      <div className="w-12 text-right font-mono-data text-xs text-muted-foreground">
                        {pct.toFixed(1)}%
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="font-display text-lg flex items-center gap-2">
                <GlobeSimple size={20} weight="duotone" className="text-primary" />
                {t("cbamByCountry")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2" data-testid="cbam-by-country">
                {liability.by_country.map((c) => {
                  const pct = totals.cbam_payable_eur > 0 ? (c.cbam_payable_eur / totals.cbam_payable_eur) * 100 : 0;
                  return (
                    <div key={c.country} className="flex items-center gap-4">
                      <div className="w-44 truncate text-sm">{c.country}</div>
                      <div className="flex-1 h-6 bg-secondary rounded-sm overflow-hidden">
                        <div className="h-full bg-accent" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="w-32 text-right font-mono-data text-sm">
                        €{c.cbam_payable_eur.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </div>
                      <div className="w-12 text-right font-mono-data text-xs text-muted-foreground">
                        {pct.toFixed(1)}%
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Phase-in schedule table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-display text-lg">{t("cbamPhaseInSchedule")}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                <tr>
                  {YEARS.map((y) => (
                    <th key={`ph-${y}`} className={`px-3 py-2 text-center font-mono-data ${y === year ? "bg-primary/10 text-primary" : ""}`}>{y}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {YEARS.map((y) => {
                    const f = settings.phase_in_schedule?.[y] || 0;
                    return (
                      <td key={`pv-${y}`} className={`px-3 py-3 text-center font-mono-data ${y === year ? "bg-primary/10 text-primary font-semibold" : ""}`}>
                        {(f * 100).toFixed(1)}%
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function CbamPreview({ product, quantity, directOverride, indirectOverride, originPrice, etsPrice, phaseIn }) {
  const direct = directOverride === "" || directOverride == null ? product.direct_default : parseFloat(directOverride);
  const indirect = indirectOverride === "" || indirectOverride == null ? product.indirect_default : parseFloat(indirectOverride);
  const embeddedDirect = quantity * direct;
  const embeddedIndirect = quantity * indirect;
  const embeddedTotal = embeddedDirect + embeddedIndirect;
  const gross = embeddedTotal * etsPrice;
  const credit = embeddedTotal * (originPrice || 0);
  const net = Math.max(0, gross - credit);
  const payable = net * phaseIn;
  return (
    <div className="font-mono-data text-xs space-y-1 leading-relaxed">
      <div>{quantity} {product.unit} × ({direct} + {indirect}) tCO₂e/{product.unit} = <span className="font-semibold">{embeddedTotal.toFixed(3)} tCO₂e</span></div>
      <div>Gross: {embeddedTotal.toFixed(3)} × €{etsPrice} = €{gross.toFixed(2)}</div>
      <div>Origin credit: −€{credit.toFixed(2)} (−{(originPrice || 0).toFixed(2)} €/tCO₂e paid at origin)</div>
      <div>Net: €{net.toFixed(2)} × phase-in {(phaseIn * 100).toFixed(1)}% = <span className="font-semibold text-primary text-sm">€{payable.toFixed(2)}</span></div>
    </div>
  );
}
